import { memo, useEffect, useRef, useState, type ReactNode } from "react";
import type { PanoIsSatiri, PanoKutucugu, PanoRiser, PanoYaniti } from "@oasis/domain";
import { tarihSaat, useVeri } from "@/lib/veri";
import { cn } from "@/lib/cn";
import { cevir } from "@/lib/dil";
import { Bos, Buton, Etiket, Hata, Kutu, tabloBas, tabloHucre } from "@/components/parcalar";
import {
  HIDROTEST_HAT,
  NDT_HAT,
  RENK_KARESI,
  acilisSecimi,
  adimHucresi,
  adimSayaci,
  aramaYaz,
  batchListesi,
  batchSec,
  bitenBasligi,
  bitenIsler,
  cubukIpucu,
  dispoSokumHucresi,
  eksikBelgeler,
  finishingSatiri,
  hapIpucu,
  hapListesi,
  isBasligi,
  isYuzdesi,
  katmanBasligi,
  katmanBosMetni,
  katmanNotu,
  kutucukAdi,
  kutucukIpucu,
  kutucukListesi,
  kutucukOzetleri,
  kutucukTikla,
  lejant,
  listeSayaci,
  oranMetni,
  panoHatKaresi,
  planSatiri,
  renkAdi,
  riserTikla,
  secimiDogrula,
  seritMetni,
  sokumSatirDurumu,
  sonSozcukAyir,
  suzgeciTemizle,
  tamirSatirlari,
  yuzdeMetni,
  type KutucukOzeti,
  type PanoSecim,
  type PanoSuzgeci,
  type TamirSatiri,
} from "@/lib/pano";
import { HAP, HAP_DIGER, HAP_SECILI, RiserDosyasi, type Sekme } from "./RiserBelgeleri";

/**
 * D-071: Dashboard. Her riserin yeniden sertifikalandırma sürecinde adım adım nerede durduğunu gösteren salt okunur pano: seçici
 * kartı (arama, batch düğmeleri, durum rengiyle riser hapları, lejant), dokuz kutucuk, seçili kutucuğun süzgeç şeridi ve ayrıntı
 * katmanı, en altta seçili riserin ayrıntısı (Riser Documentation'daki bileşenin kendisi; yükleyen adları gizli). Hiçbir düğme veri
 * yazmaz; batch ve kutucuk süzgeci tarayıcıda çalışır, tıklama yeni istek atmaz (riser ayrıntısı dışında).
 *
 * Süzme, sayma, seçim geçişleri ve sayılı cümlelerin hepsi lib/pano.ts'tedir; bu dosya yalnız çağırır ve çizer. İki küme karışmasın:
 * `batchRiserleri` = batchListesi (kutucuk sayıları, katman alt notları, "Completed (n)"), `kutucukRiserleri` = kutucukListesi
 * (şeritteki sayı, katmanın ana satırları). Sınıflar onaylı statik sayfalardan (prototip/sayfalar/dashboard*.html) alındı.
 *
 * Seçili hap ve seçili kutucuğun turuncu çerçevesi ünlemli sınıfla yazılır (`border-orange!`): styles.css'teki katman dışı
 * `* { border-color }` kuralı ünlemsiz çerçeve rengi sınıflarını ezer. Sınıf dizeleri tam yazılır, birleştirilerek kurulmaz.
 */

/** Riser ayrıntısındaki bir bağlantıyla başka ekrana gidip dönünce süzgeç kaybolmasın: yalnız süzgeç hatırlanır, seçili riser değil. */
let suzgec: PanoSuzgeci = { batchNo: null, kutucuk: null, arama: "" };

const RISER_DUGMESI = "font-mono font-bold underline underline-offset-2";
const KARE = "inline-block h-3.5 w-3.5 shrink-0 rounded-[3px]";
const ALT_NOT = "border-t border-line px-4 py-2 text-xs text-ink-3";
const IS_SATIRI = "flex items-center gap-x-2 border-b border-dashed border-line py-1.5 text-sm last:border-0";
const IS_BASLIK_SUTUNU = "w-64 shrink-0 min-[1440px]:w-80";

export function Pano() {
  const { veri, hata } = useVeri<PanoYaniti>("/pano");
  const [ham, setHam] = useState<PanoSecim>(() => acilisSecimi(suzgec));
  const [sekme, setSekme] = useState<Sekme>("belgeler");
  /** Riser tıklanınca konur; ayrıntı çizildikten sonra oraya kaydırılır. Sayfa açılışında riser seçili değildir, kayma olmaz. */
  const kaydir = useRef(false);
  // Hatırlanan ya da açık duran seçim, gelen veriyle her çizimde doğrulanır (artık olmayan batch, sistemde olmayan riser).
  const secim = veri ? secimiDogrula(ham, veri) : ham;

  useEffect(() => {
    if (!kaydir.current) return;
    kaydir.current = false;
    document.getElementById("riser-details")?.scrollIntoView({ block: "start", behavior: "smooth" });
  }, [secim.rn]);

  const sec = (yeni: PanoSecim) => {
    suzgec = { batchNo: yeni.batchNo, kutucuk: yeni.kutucuk, arama: yeni.arama };
    setHam(yeni);
  };

  return (
    <div className="space-y-4">
      <PanoBasligi uretim={veri?.uretim ?? null} />
      {hata ? (
        <Hata mesaj={hata} />
      ) : !veri ? (
        <Bos>{cevir("Loading...")}</Bos>
      ) : (
        <PanoGovdesi
          veri={veri}
          secim={secim}
          sec={sec}
          riserSec={(rn) => {
            const yeni = riserTikla(secim, rn);
            // Riser açılıyorsa (seçili risere ikinci tıklama kapatır): ayrıntı her seferinde "Documents on file" sekmesiyle açılır ve oraya kaydırılır.
            if (yeni.rn !== null) setSekme("belgeler");
            kaydir.current = yeni.rn !== null;
            sec(yeni);
          }}
        />
      )}
      {!hata && veri && secim.rn && <RiserAyrintisi rn={secim.rn} sekme={sekme} setSekme={setSekme} />}
    </div>
  );
}

/** Başlık. `Sayfa` bileşeni kullanılmaz: onun başlık satırı kırılır ve "Data as of" 1280 px'te alta düşerdi. Sayfadaki tek tarih budur. */
function PanoBasligi({ uretim }: { uretim: string | null }) {
  return (
    <div className="flex items-end justify-between gap-3">
      <div className="min-w-0 flex-1">
        <div className="text-xs font-semibold uppercase tracking-[0.1em] text-ink-3">{cevir("DS Yavuz · riser recertification")}</div>
        <h1 className="text-xl font-semibold tracking-tight sm:text-2xl">{cevir("Dashboard")}</h1>
        <p className="mt-0.5 max-w-4xl text-base leading-relaxed text-ink-2">
          {cevir("Where every riser stands, step by step. Pick a batch to narrow the list, a tile to see the risers it counts, and a riser to open its documents at the bottom of the page. This page is read-only.")}
        </p>
      </div>
      {uretim && (
        <div className="flex shrink-0 flex-col items-end gap-1 text-xs text-ink-3">
          <span className="whitespace-nowrap tnum">{cevir("Data as of {0}", tarihSaat(uretim))}</span>
        </div>
      )}
    </div>
  );
}

function PanoGovdesi({ veri, secim, sec, riserSec }: { veri: PanoYaniti; secim: PanoSecim; sec: (s: PanoSecim) => void; riserSec: (rn: string) => void }) {
  const batchRiserleri = batchListesi(veri.riserler, secim.batchNo);
  const kutucukRiserleri = kutucukListesi(veri.riserler, secim);
  const haplar = hapListesi(veri.riserler, secim);
  const ozetler = kutucukOzetleri(batchRiserleri, veri.bagsizMrb);
  const seciliOzet = ozetler.find((o) => o.kutucuk === secim.kutucuk);
  return (
    <>
      <PanoSecici
        veri={veri}
        secim={secim}
        haplar={haplar}
        onArama={(arama) => sec(aramaYaz(secim, arama))}
        onBatch={(batchNo) => sec(batchSec(secim, batchNo, veri.riserler))}
        onRiser={riserSec}
      />
      <PanoKutucuklari ozetler={ozetler} secili={secim.kutucuk} onTikla={(k) => sec(kutucukTikla(secim, k, veri.riserler))} />
      {seciliOzet && (
        <>
          <SuzgecSeridi ozet={seciliOzet} batchNo={secim.batchNo} riserSayisi={kutucukRiserleri.length} onTemizle={() => sec(suzgeciTemizle(secim, veri.riserler))} />
          <PanoKatmani ozet={seciliOzet} batchRiserleri={batchRiserleri} kutucukRiserleri={kutucukRiserleri} isler={veri.isler} bagsizMrb={veri.bagsizMrb} onRiser={riserSec} />
        </>
      )}
    </>
  );
}

/** Seçici kartı: arama, All ve batch düğmeleri, çizilen hap sayısı; altında riser hapları (RN sırasıyla) ve lejant. */
function PanoSecici({
  veri,
  secim,
  haplar,
  onArama,
  onBatch,
  onRiser,
}: {
  veri: PanoYaniti;
  secim: PanoSecim;
  haplar: PanoRiser[];
  onArama: (arama: string) => void;
  onBatch: (batchNo: string | null) => void;
  onRiser: (rn: string) => void;
}) {
  return (
    <Kutu padded={false}>
      <div className="flex flex-wrap items-center gap-2 px-4 py-3">
        <input
          className="h-kontrol-kucuk rounded-md border border-line bg-surface px-3 text-sm"
          placeholder={cevir("Search riser no. or Assembly SN...")}
          aria-label={cevir("Search riser no. or Assembly SN...")}
          value={secim.arama}
          onChange={(e) => onArama(e.target.value)}
        />
        {[null, ...veri.batchler.map((b) => b.no)].map((b) => (
          <button key={b ?? ""} type="button" aria-pressed={secim.batchNo === b} onClick={() => onBatch(b)} className={cn(HAP, "inline-flex items-center", secim.batchNo === b ? HAP_SECILI : HAP_DIGER)}>
            {b ?? cevir("All")}
            <span className="ml-1 opacity-70 tnum">{b === null ? veri.riserler.length : veri.batchler.find((x) => x.no === b)?.riser}</span>
          </button>
        ))}
        <span className="ml-auto text-xs text-ink-3">{listeSayaci(haplar.length)}</span>
      </div>
      <div className="flex flex-wrap gap-1 border-t border-line px-4 py-3">
        {haplar.map((r) => (
          <button
            key={r.rn}
            type="button"
            aria-pressed={r.rn === secim.rn}
            title={hapIpucu(r)}
            onClick={() => onRiser(r.rn)}
            className={cn(HAP, "inline-flex items-center gap-1.5", r.rn === secim.rn ? "border-orange! bg-orange-soft text-ink" : HAP_DIGER)}
          >
            <i className={cn(KARE, RENK_KARESI[r.renk])} />
            {r.rn}
          </button>
        ))}
        {!haplar.length && <span className="text-sm text-ink-3">{cevir("No riser in this filter.")}</span>}
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-line px-4 py-2 text-xs text-ink-2">
        {lejant(haplar, secim.kutucuk !== null).map((x) => (
          <span key={x.renk}>
            <i className={cn("mr-1 align-middle", KARE, RENK_KARESI[x.renk])} />
            {renkAdi(x.renk)} <b className="tnum">{x.sayi}</b>
          </span>
        ))}
        <span className="ml-auto text-ink-3">{cevir("Colour = the furthest stage the riser has reached. A missing MRB does not change it.")}</span>
      </div>
    </Kutu>
  );
}

/**
 * Dokuz kutucuk: 1280 px ve üstünde tek sırada, daha dar ekranda 3 + 3 + 3, telefonda ikişer. Kutucuğun beş satırı (ad, büyük sayı,
 * "pay / payda söz · yüzde", çubuk, not) alt ızgarayla dokuz kutucukta aynı hizadadır. Kutucuk düğmedir: tıklanınca seçilir.
 * Dokuz kutucuk 1280 px'te ancak sığar (içerik alanı 977 px): 1280 ile 1439 px arasında aralık ve yan iç boşluk daraltılır
 * (`xl:gap-1.5`, `xl:px-2`), 1440 px ve üstünde onaylı ölçülere döner (`min-[90rem]`; `xl` rem ile tanımlı olduğu için eşik de rem
 * yazılır, yoksa derlenen stilde sırası belirsiz kalır). Daraltılmazsa en uzun sözlerle ızgara sağa taşar.
 */
function PanoKutucuklari({ ozetler, secili, onTikla }: { ozetler: KutucukOzeti[]; secili: PanoKutucugu | null; onTikla: (k: PanoKutucugu) => void }) {
  return (
    <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 xl:grid-cols-[repeat(9,minmax(min-content,1fr))] xl:gap-1.5 min-[90rem]:gap-2.5">
      {ozetler.map((o) => {
        const [bas, son] = sonSozcukAyir(o.soz);
        const oranli = o.pay !== null && o.payda !== null;
        return (
          <button
            key={o.kutucuk}
            type="button"
            aria-pressed={o.kutucuk === secili}
            title={kutucukIpucu(o)}
            onClick={() => onTikla(o.kutucuk)}
            className={cn(
              "row-span-5 grid min-w-0 grid-rows-subgrid gap-y-1 rounded-lg border p-3 text-left shadow-[var(--shadow)] transition xl:px-2 min-[90rem]:px-3",
              o.kutucuk === secili ? "border-orange! bg-orange-soft" : "border-line bg-surface hover:border-line-strong",
            )}
          >
            <span className="text-xs font-medium leading-tight text-ink-2">{kutucukAdi(o.kutucuk)}</span>
            <span className="mt-0.5 whitespace-nowrap text-3xl font-semibold leading-none tnum text-ink">{o.buyuk}</span>
            {oranli ? (
              <span className="mt-0.5 text-xs leading-tight text-ink-2">
                <span className="tnum">{o.pay} / {o.payda}</span> {bas && `${bas} `}
                <span className="whitespace-nowrap">{son} · <span className="tnum">{yuzdeMetni(o.yuzde)}</span></span>
              </span>
            ) : (
              <span className="mt-0.5 text-xs leading-tight text-ink-2">{o.soz}</span>
            )}
            {oranli ? (
              <span className="mt-0.5 flex h-1.5 w-full self-center overflow-hidden rounded-full bg-surface-3">
                <i className="block h-full bg-ok" style={{ width: `${o.yuzde ?? 0}%` }} />
              </span>
            ) : (
              <span />
            )}
            <span className="mt-0.5 text-2xs leading-snug text-ink-3">{o.not}</span>
          </button>
        );
      })}
    </div>
  );
}

/** Süzgeç şeridi: seçili kutucuğun adı, listenin kaç risere indiği ve "Clear filter" (kutucuğu kaldırır, batch kalır). */
function SuzgecSeridi({ ozet, batchNo, riserSayisi, onTemizle }: { ozet: KutucukOzeti; batchNo: string | null; riserSayisi: number; onTemizle: () => void }) {
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-line bg-orange-soft px-3 py-2 text-sm">
      <span className="font-semibold">{kutucukAdi(ozet.kutucuk)}</span>
      <span className="text-ink-2">{seritMetni(batchNo, riserSayisi)}</span>
      <Buton kucuk className="ml-auto" onClick={onTemizle}>{cevir("Clear filter")}</Buton>
    </div>
  );
}

/**
 * Seçili kutucuğun ayrıntı katmanı. Ana satırlar `kutucukRiserleri`nden (Repair'de komponent satırları: açık işler ve planlama bekleyen komponentler), alt notlar
 * ve "Completed (n)" `batchRiserleri`nden gelir. Kişi adı ve kayıt tarihi yoktur.
 */
function PanoKatmani({
  ozet,
  batchRiserleri,
  kutucukRiserleri,
  isler,
  bagsizMrb,
  onRiser,
}: {
  ozet: KutucukOzeti;
  batchRiserleri: PanoRiser[];
  kutucukRiserleri: PanoRiser[];
  isler: PanoIsSatiri[];
  bagsizMrb: number;
  onRiser: (rn: string) => void;
}) {
  const k = ozet.kutucuk;
  const { cumle, altBaslik } = katmanBasligi(ozet);
  const not = katmanNotu(k, batchRiserleri, bagsizMrb);
  return (
    <Kutu baslik={`${kutucukAdi(k)} · ${cumle}`} aksiyon={altBaslik} padded={false}>
      {k === "tamir" ? (
        <TamirKatmani satirlar={tamirSatirlari(isler, batchRiserleri)} biten={bitenIsler(isler, batchRiserleri)} onRiser={onRiser} />
      ) : (
        <RiserTablosu k={k} riserler={kutucukRiserleri} onRiser={onRiser} />
      )}
      {not && <div className={ALT_NOT}>{not}</div>}
    </Kutu>
  );
}

/** Katmandaki riser numarası: tıklanınca riser ayrıntısı en altta açılır (seçili risere ikinci tıklama kapatır). */
function RiserDugmesi({ rn, onRiser }: { rn: string; onRiser: (rn: string) => void }) {
  return (
    <button type="button" className={RISER_DUGMESI} title={cevir("Open riser details")} onClick={() => onRiser(rn)}>
      {rn}
    </button>
  );
}

type Kolon = { ad: string; ipucu?: string; hucre: (r: PanoRiser) => ReactNode };
const soluk = (metin: string) => <span className="text-ink-3">{metin}</span>;
const hatKareleri = (r: PanoRiser) => (
  <span className="inline-flex gap-1 align-middle">
    {r.hatlar.map((h) => {
      const kare = panoHatKaresi(h);
      return <span key={h.hat} title={kare.ipucu} className={cn("inline-block h-3.5 w-3.5 rounded-[3px]", kare.sinif)} />;
    })}
  </span>
);

/**
 * Katman tablosunun Riser ve Batch'ten sonraki kolonları. Assembly ve Done aynı kolonları taşır; yalnız Status hücresi ayrılır:
 * Assembly'de Coating raporu ya da hidrotest eksikse eksik olanı söyleyen etiket(ler), hepsi tamamsa "Assembly report pending".
 */
function kolonlar(k: Exclude<PanoKutucugu, "tamir">): Kolon[] {
  switch (k) {
    case "mrb":
      return [
        { ad: cevir("Assembly SN"), hucre: (r) => (r.assemblySn ? <span className="font-mono">{r.assemblySn}</span> : soluk("—")) },
        { ad: cevir("Status"), hucre: () => <Etiket tone="warn">{cevir("MRB not received")}</Etiket> },
      ];
    case "teslim":
      return [{ ad: cevir("Status"), hucre: () => <Etiket tone="warn">{cevir("Not received")}</Etiket> }];
    case "sokum":
      return [
        {
          ad: cevir("Status"),
          hucre: (r) => {
            const d = sokumSatirDurumu(r);
            return <Etiket tone={d.ton}>{d.metin}</Etiket>;
          },
        },
      ];
    case "dispo":
      return [
        {
          ad: cevir("Disassembly"),
          hucre: (r) => {
            const d = dispoSokumHucresi(r);
            return d.soluk ? soluk(d.metin) : d.metin;
          },
        },
        { ad: cevir("Status"), hucre: () => <Etiket tone="warn">{cevir("Disposition report not uploaded")}</Etiket> },
      ];
    case "rework":
      return [
        { ad: cevir("Open rework items"), hucre: (r) => <span className="tnum">{r.reworkAcik}</span> },
        { ad: cevir("Status"), hucre: () => <Etiket tone="warn">{cevir("Rework report not uploaded")}</Etiket> },
      ];
    case "finishing":
      return [
        { ad: cevir("Lines"), hucre: hatKareleri },
        { ad: cevir("Progress"), hucre: (r) => <span className="whitespace-nowrap tnum">{finishingSatiri(r).oran}</span> },
        {
          ad: cevir("Lines not in Finishing"),
          hucre: (r) => (
            <span className="inline-flex flex-wrap gap-1">
              {finishingSatiri(r).eksikler.map((e) => <Etiket key={e.metin} tone={e.ton}>{e.metin}</Etiket>)}
            </span>
          ),
        },
      ];
    case "montaj":
    case "tamam":
      return [
        { ad: cevir("Lines"), hucre: hatKareleri },
        { ad: cevir("Coating report"), hucre: (r) => (r.boyaRaporu ? <Etiket tone="ok">{cevir("uploaded")}</Etiket> : soluk(cevir("missing"))) },
        { ad: cevir("Hydrotest"), hucre: (r) => <span className="tnum">{oranMetni(r.hidrotest, HIDROTEST_HAT)}</span> },
        // "NDT" kısaltması çevrilmez; yalnız bilgidir (ipucu söyler).
        { ad: "NDT", ipucu: cevir("For information only: NDT reports are not required for a riser to be ready for assembly"), hucre: (r) => <span className="tnum text-ink-2">{oranMetni(r.ndt, NDT_HAT)}</span> },
        {
          ad: cevir("Status"),
          hucre: (r) => {
            if (k === "tamam") return <Etiket tone="ok">{cevir("Assembly report uploaded")}</Etiket>;
            const eksik = eksikBelgeler(r);
            if (!eksik.length) return <Etiket tone="warn">{cevir("Assembly report pending")}</Etiket>;
            return (
              <span className="inline-flex flex-wrap gap-1">
                {eksik.map((e) => <Etiket key={e}>{e}</Etiket>)}
              </span>
            );
          },
        },
      ];
  }
}

/** Riser başına bir satır: MRB Intake, Receiving, Disassembly, Disposition Report, Rework Scope, Finishing, Assembly ve Done katmanları. */
function RiserTablosu({ k, riserler, onRiser }: { k: Exclude<PanoKutucugu, "tamir">; riserler: PanoRiser[]; onRiser: (rn: string) => void }) {
  if (!riserler.length) return <div className="px-4 py-3 text-sm text-ink-3">{katmanBosMetni(k)}</div>;
  const ek = kolonlar(k);
  return (
    <div className="scroll-thin overflow-x-auto">
      <table className="w-full min-w-[620px]">
        <thead>
          <tr className="border-b border-line">
            <th className={tabloBas}>{cevir("Riser")}</th>
            <th className={tabloBas}>{cevir("Batch")}</th>
            {ek.map((x) => <th key={x.ad} className={tabloBas} title={x.ipucu}>{x.ad}</th>)}
          </tr>
        </thead>
        <tbody>
          {riserler.map((r) => (
            <tr key={r.rn} className="border-b border-line last:border-0 hover:bg-surface-2">
              <td className={tabloHucre}><RiserDugmesi rn={r.rn} onRiser={onRiser} /></td>
              <td className={tabloHucre}>{r.batchNo ?? soluk("—")}</td>
              {ek.map((x) => <td key={x.ad} className={tabloHucre}>{x.hucre(r)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Repair katmanı: her açık iş ve tamir planlaması bekleyen her komponent için ince tek satır, tarihsiz (riser, sonra beş hat, sonra öteki komponentler). Baştaki üç sütun sabit genişliktedir (çubuklar aynı hizada başlar);
 * son hücre kalan yeri alır, sığmazsa metnin sonu kesilir (tamamı ipucunda), rozet hep görünür. Altında kapalı gelen "Completed (n)".
 */
function TamirKatmani({ satirlar, biten, onRiser }: { satirlar: TamirSatiri[]; biten: PanoIsSatiri[]; onRiser: (rn: string) => void }) {
  return (
    <div className="scroll-thin overflow-x-auto px-4 py-3">
      <div className="min-w-[720px]">
        {satirlar.length ? (
          <>
            <div className="flex items-center gap-x-2 border-b border-line pb-1 text-xs font-medium text-ink-3">
              <span className={IS_BASLIK_SUTUNU}>{cevir("Component")}</span>
              <span className="w-32 shrink-0">{cevir("Progress")}</span>
              <span className="w-28 shrink-0" />
              <span className="min-w-0 flex-1 truncate">{cevir("Last passed step ✔ - current step")}</span>
            </div>
            {satirlar.map((s) => (s.tur === "is" ? <IsSatiri key={s.is.id} s={s.is} onRiser={onRiser} /> : <PlanSatiri key={`${s.rn}-${s.komponent}`} s={s} onRiser={onRiser} />))}
          </>
        ) : (
          <div className="text-sm text-ink-3">{katmanBosMetni("tamir")}</div>
        )}
        <details className="group mt-3">
          <summary className="cursor-pointer list-none text-sm font-semibold text-ink-2 hover:text-ink">
            <span className="inline-block w-4 transition group-open:rotate-90">▶</span>
            {bitenBasligi(biten.length)}
          </summary>
          {biten.map((s) => <IsSatiri key={s.id} s={s} />)}
        </details>
      </div>
    </div>
  );
}

/** Bir tamir işi. Açık işte riser numarası düğmedir; biten işte (`onRiser` yok) kalın yazıdır, tıklanmaz. */
function IsSatiri({ s, onRiser }: { s: PanoIsSatiri; onRiser?: (rn: string) => void }) {
  const baslik = isBasligi(s);
  const adim = adimHucresi(s);
  const yuzde = isYuzdesi(s);
  return (
    <div className={IS_SATIRI}>
      <span className={cn(IS_BASLIK_SUTUNU, "truncate")} title={baslik.ipucu}>
        {onRiser ? <RiserDugmesi rn={s.rn} onRiser={onRiser} /> : <b className="font-mono">{s.rn}</b>}
        {baslik.ust && ` · ${baslik.ust}`} <span className="text-xs text-ink-3">{baslik.isAdi}</span>
      </span>
      <span className="inline-flex h-2 w-32 shrink-0 overflow-hidden rounded-full bg-surface-3" title={cubukIpucu(s)}>
        <i className="block h-full bg-ok" style={{ width: `${yuzde}%` }} />
      </span>
      <span className="w-28 shrink-0 whitespace-nowrap">
        <span className="tnum">{yuzde}%</span> <span className="text-xs text-ink-3">{adimSayaci(s)}</span>
      </span>
      <span className="flex min-w-0 flex-1 items-center gap-1.5">
        <span className="min-w-0 truncate" title={adim.ipucu}>
          {adim.sol.tur === "yok" ? soluk("—") : <>{adim.sol.ad} {adim.sol.tur === "gecti" ? <span className="text-ok">✔</span> : soluk(cevir("no record"))}</>}
          {" - "}
          {adim.sag && <>{adim.sag.soluk ? soluk(adim.sag.metin) : adim.sag.metin} ·</>}
        </span>
        <Etiket tone={adim.rozet.ton}>{adim.rozet.metin}</Etiket>
      </span>
    </div>
  );
}

/** Tamir planlaması bekleyen komponent (hat, Seal Sub ya da Fastener): iş satırının hizasında boş çubuk ve "0%"; son hücrede soluk "—", "awaiting planning" rozeti ve bulgu sayısı. */
function PlanSatiri({ s, onRiser }: { s: Extract<TamirSatiri, { tur: "plan" }>; onRiser: (rn: string) => void }) {
  const plan = planSatiri(s);
  return (
    <div className={IS_SATIRI}>
      <span className={cn(IS_BASLIK_SUTUNU, "truncate")} title={plan.baslikIpucu}>
        <RiserDugmesi rn={s.rn} onRiser={onRiser} />
        {` · ${plan.ust}`}
      </span>
      <span className="inline-flex h-2 w-32 shrink-0 overflow-hidden rounded-full bg-surface-3" />
      <span className="w-28 shrink-0 whitespace-nowrap">
        <span className="tnum">0%</span>
      </span>
      <span className="flex min-w-0 flex-1 items-center gap-1.5" title={plan.ipucu}>
        {soluk("—")}
        <Etiket tone="warn">{plan.rozet}</Etiket>
        <span className="truncate text-xs text-ink-3">{plan.bulgu}</span>
      </span>
    </div>
  );
}

/**
 * Riser ayrıntısı: Riser Documentation'daki bileşenin kendisi, sayfanın en altında. Dashboard'da yükleyen adları, yükleme günleri ve
 * beyan edenin adı çizilmez; iki bilgi bandı ve başlıktaki beş hat karesi de çizilmez (Bekir, 11.10.2026). "Reference" bölümü, işlem
 * ekranı adları ve bağlantıları yerindedir. Gizleme yalnız çizimdedir: uç adları göndermeye devam eder. D-074 Faz 2 · PR 8 (T30): Documents
 * okumasının çipleri ve HT-08 satırları da çizilmez (`okumaGizli`).
 * Kap bir ekran boyu yer tutar: ayrıntının verisi gelmeden önce de kaydırma hedefi ekranın üstünde hizalanır.
 * `memo`: arama kutusuna yazmak ya da kutucuk değiştirmek ayrıntıyı yeniden çizmez.
 */
const RiserAyrintisi = memo(function RiserAyrintisi({ rn, sekme, setSekme }: { rn: string; sekme: Sekme; setSekme: (s: Sekme) => void }) {
  return (
    <div id="riser-details" className="min-h-[calc(100dvh-5rem)] space-y-4 scroll-mt-20">
      <RiserDosyasi key={rn} rn={rn} sekme={sekme} setSekme={setSekme} yukleyenGizli bantGizli hatKareleriGizli okumaGizli />
    </div>
  );
});
